import React, { useMemo } from 'react';

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import clsx from 'clsx';
import {
  AlineAvatar,
  CenteredLoader,
  permissionListCellHeight,
  StorablePermissionRow,
  usePermissionRowStyles,
} from 'src/components';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { getEntityDisplayDetails } from 'src/helpers';
import { useGetGroupsWithDocAccess, useGetStorablePermissionsWithStorable } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { GroupPermissionRow } from './GroupPermissionRow';

const useStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'center',
  },
  emptyText: {
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    boxSizing: 'border-box',
    maxHeight: permissionListCellHeight * 6,
    minHeight: permissionListCellHeight * 3,
    overflowY: 'auto',
  },
}));

interface PermissionListProps {
  documentId: UUID;
  disabled?: boolean;
  listClassName?: string;
}

export function PermissionList({
  disabled,
  documentId,
  listClassName,
}: PermissionListProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const { classes: rowClasses } = usePermissionRowStyles();
  const { loading: loadingStorablePermissions, storablePermissions } =
    useGetStorablePermissionsWithStorable(documentId, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });
  const {
    document,
    groupsWithAccess,
    loading: loadingDocumentGroups,
  } = useGetGroupsWithDocAccess(documentId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { sortedDocumentPermissions, sortedGroupsWithAccess } = useMemo(() => {
    const sortedDocumentPermissions = [...storablePermissions].sort((a, b) => {
      const aName = a.account.__typename === 'User' ? a.account.displayName : a.account.name;
      const bName = b.account.__typename === 'User' ? b.account.displayName : b.account.name;
      return aName.localeCompare(bName);
    });
    const sortedGroupsWithAccess = [...groupsWithAccess].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    return { sortedDocumentPermissions, sortedGroupsWithAccess };
  }, [groupsWithAccess, storablePermissions]);
  const loading = loadingStorablePermissions || loadingDocumentGroups;

  if (loading || !document) return <CenteredLoader className={classes.tableContainer} />;

  const { primary, secondary } = getEntityDisplayDetails(document.owner);

  return (
    <TableContainer className={clsx(classes.tableContainer, listClassName)}>
      <Table>
        <TableBody>
          <TableRow className={rowClasses.tableRow}>
            <TableCell className={rowClasses.tableCell} style={{ paddingRight: theme.spacing(1) }}>
              <AlineAvatar avatarInfo={document.owner} />
            </TableCell>
            <TableCell className={clsx(rowClasses.tableCell, rowClasses.nameCell)}>
              <div
                className={rowClasses.authorCellWrapper}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <Typography variant="body1">{primary}</Typography>
                {secondary && secondary !== primary && (
                  <Typography variant="caption">{secondary}</Typography>
                )}
              </div>
            </TableCell>
            <TableCell className={rowClasses.roleCell} style={{ paddingRight: theme.spacing(1) }}>
              <Typography className={rowClasses.roleText} variant="body1">
                Owner
              </Typography>
            </TableCell>
            <TableCell className={rowClasses.tableCell}>
              <IconButton disabled size="small" style={{ visibility: 'hidden' }}>
                <TrashcanIcon />
              </IconButton>
            </TableCell>
          </TableRow>
          {sortedGroupsWithAccess.map(group => {
            return <GroupPermissionRow key={group.id} group={group} />;
          })}
          {sortedDocumentPermissions.map(permission => {
            return (
              <StorablePermissionRow
                key={permission.id}
                disabled={disabled}
                permission={permission}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
